<script lang="ts" setup generic="T">
	import type { RecordSchema } from '@/types'
	import type { SelectValue } from 'ant-design-vue/es/select'
	import usePurchaseApi from '~/services/apis/purchases-info'
	import StringMethods from '~/utils/character'

	type EventListeners = {
		validateEvent: {
			param: Record<string, DynamicTableRecord>
			callback: (arg: boolean) => void
			event: 'validator'
		}
	}
	const { verifyEmail = true, ...props } = defineProps<{
		formProps?: unknown
		data: RecordsFn<T>
		onValidator?: Function
		submitting?: boolean
		skipUserVerification?: boolean
		verifyEmail?: boolean
	}>()
	const emits = defineEmits<{
		(event: 'update:value', param: DynamicTableContext[]): void
		(arg: 'done', param: SubmissionContext[]): void
		(arg: 'add-field', param?: unknown): Promise<void>
		(
			arg: 'validator',
			param: EventListeners['validateEvent']['param'],
			callback: EventListeners['validateEvent']['callback']
		): Promise<boolean>
		(arg: 'update-records', param: unknown): Promise<void>
	}>()
	const { toCapitalize, stripUnderScore } = StringMethods()

	const {
		data: responseResult,
		paginationFilter,
		getOptions
	} = useScrollPaginator<T>({
		filterKey: 'temp_id',
		fetchOptions: props.data,
		elementId: 'body'
	})
	type SubmissionContext = { [id: string]: number | string }
	const isAnItemDone = computed((): boolean => {
		return Object.values(responseResult.value).some(item => (item as RecordSchema).done)
	})

	const validateTableField = (record: DynamicTableContext) => {
		const responseRecord = record as { [id: string]: DynamicTableRecord }
		for (const key in record) {
			if (responseRecord[key]?.validations) {
				let message = ''
				const validations = responseRecord[key].validations
				if (validations) {
					if (validations.required && !responseRecord[key].value) {
						message = (validations?.message as string) || `${stripUnderScore(key)} is required`
					} else if (validations.min && String(responseRecord[key].value).length < validations.min) {
						message =
							(validations?.message as string) ||
							`${stripUnderScore(key)} must be at least ${validations.min} characters long`
					} else if (validations.max && String(responseRecord[key].value).length > validations.max) {
						message =
							(validations?.message as string) ||
							`${stripUnderScore(key)} must be at less or equal to ${validations.min}`
					}
				}
				if (message) {
					useNotification().alert({
						status: 400,
						message
					})
					return Promise.reject(message)
				}
			}
		}
		return Promise.resolve(true)
	}
	const isFieldValidated = (param: unknown, fn: (res: boolean) => void) => {
		if (!props?.onValidator) {
			fn(true)
		}
		param.loading = true
		emits('validator', param, async response => {
			try {
				const result = await response
				if (result) {
					param.loading = false
					fn(true)
				}
			} catch (error) {
				console.warn(error)
			} finally {
				param.loading = false
			}
		})
	}
	/**
	 * Extracts and flattens values from a list of DynamicTableContext records.
	 * Only processes records that are marked as `done`. For each record, it iterates
	 * through its keys and extracts the primitive value or the `.value` property if it's an object.
	 *
	 * @param {DynamicTableContext[]} records - The list of dynamic table records to process.
	 * @returns {SubmissionContext[]} An array of cleaned up objects ready for submission.
	 *
	 * @example
	 * const records = [
	 *   { done: true, first_name: { value: 'John', error: false }, age: 30 },
	 *   { done: false, first_name: { value: 'Jane' } }
	 * ];
	 * const values = extractFormValues(records);
	 * // values = [{ first_name: 'John', age: '30', done: 'true' }]
	 */
	const extractFormValues = (records: DynamicTableContext[]) => {
		const dataForm: SubmissionContext[] = []
		const resultList = records.filter(item => item.done)
		// Structure the data to strip validation keys
		for (let index = 0; index < resultList.length; index++) {
			const record = resultList[index]
			const emptyRecord: SubmissionContext = {}
			for (const key in record) {
				const value = record[key]
				const valuesResult =
					typeof value === 'object' ? value.value : typeof value === 'boolean' ? 'true' : value
				if (valuesResult) {
					Object.defineProperty(emptyRecord, key, {
						value: String(valuesResult).trim(),
						writable: true,
						enumerable: true
					})
				}
			}
			dataForm.push(emptyRecord)
		}
		return Promise.resolve(dataForm)
	}
	const { verifyTableUserRecord } = usePurchaseApi()
	const isEmailAndPhoneLinked = async (payload: { email: string; phone: string }) => {
		if (verifyEmail) {
			const res = await verifyTableUserRecord({ ...payload, phone: replacePhoneNumberToNaija(payload.phone) })
			if (res?.data) {
				const isLinked = res.data
				return isLinked
			}
		}
		return false
	}
	const verifyEmailInfo = async (param: DynamicTableContext, index: number) => {
		if (props?.skipUserVerification) {
			return true
		}
		try {
			const fieldResponse = param as Record<string, DynamicTableRecord>
			const verificationPayload = {
				email: String(fieldResponse?.email?.value ?? ''),
				phone: fieldResponse?.phone?.value ?? fieldResponse?.phone_number?.value ?? '',
				role_number: Number(index) + 1
			}
			param.loading = true
			try {
				const accountNotLinked = await isEmailAndPhoneLinked(verificationPayload)
				if (accountNotLinked) {
					return true
				}
				return false
			} finally {
				param.loading = false
			}
		} catch (e) {
			useNotification().alert({
				status: 400,
				message: 'Something went wrong, try again later'
			})
		}
		return false
	}
	const isRecordEmpty = (obj: DynamicTableContext): boolean => {
		const { done, editable, isActive, sn, ...rest } = obj
		if (rest && typeof rest === 'object') {
			for (const key in rest) {
				const res = rest[key]
				if (
					typeof res === 'object' &&
					(res?.value === undefined ||
						res?.value === null ||
						res.value === 'n/a' ||
						String(res.value).trim() === '')
				) {
					return true
				}
			}
		}
		return false
	}
	function addField(record?: DynamicTableContext, index = 0) {
		const responseRecord = record as { [id: string]: DynamicTableRecord }
		isFieldValidated(responseRecord, async canProceed => {
			if (canProceed) {
				return resolve(async () => {
					const canAdd = await validateTableField(responseRecord)
					if (record && !isRecordEmpty(record)) {
						if (record && canAdd) {
							const verified = await verifyEmailInfo(record, index)
							if (verified) {
								record.done = true
								record.editable = false
								record.isActive = false
								const formResult = await extractFormValues(responseResult.value)
								emits('update:value', formResult)
							}
						}
					} else {
						useNotification().alert({
							status: 400,
							message: 'Please make sure to fill the required fields'
						})
					}
				})
			} else {
				useNotification().alert({
					message: 'Please make sure to fill the required fields',
					status: 400
				})
			}
		})
	}
	async function editRecord(i: number) {
		const record = responseResult.value[i]
		record.done = false
		record.editable = true
		const formResult = await extractFormValues(responseResult.value)
		emits('update:value', formResult)
	}
	const saveForm = () => {
		extractFormValues(responseResult.value).then(dataForm => {
			emits('done', dataForm)
		})
	}

	const recordResponse = (records: object | null, recordIndex: number) => {
		if (records && Object.keys(records).length > 0) {
			const recordKey = Object.keys(records)[recordIndex]
			if (recordKey) {
				return (records as DynamicTableContext)[recordKey] as DynamicTableRecord
			}
			return null
		}
		return null
	}
	onMount(getOptions)
</script>

<template>
	<div>
		<a-form :model="responseResult" :validate-messages="antValidateMessages" v-bind="formProps || {}">
			<ul class="relative mb-16 mt-5 space-y-5">
				<loader-skeleton v-if="paginationFilter.loading" :height="60" :max-width="false" :count="5" />

				<template v-if="responseResult && Array.isArray(responseResult)">
					<li
						v-for="(slotItem, slotIndex) in responseResult"
						:key="`staff-form-${slotIndex}`"
						class="border-b pb-3 pt-5"
					>
						<div class="mb-2 flex items-center justify-between">
							<span class="text-base font-medium text-gray-700"> Staff {{ slotIndex + 1 }} </span>

							<div class="gap-x-4" :class="{ 'inline-flex': slotItem?.done, hidden: !slotItem?.done }">
								<button
									type="button"
									name="more actions"
									class="flex items-center gap-x-2"
									@click="editRecord(slotIndex)"
								>
									<lazy-tools-icon dir="line" icon="more-2" :height="20" :width="20" />
								</button>
								<lazy-tools-icon dir="fill" icon="check-2" :height="20" :width="20" />
							</div>
						</div>

						<div
							class="sized-container custom-transition space-y-3 overflow-hidden overflow-y-auto pt-2"
							:class="{
								'max-h-[1000px]': !slotItem.done || slotItem?.editable,
								'max-h-[0]': slotItem.done && !slotItem.editable
							}"
						>
							<slot name="form-item" :form="slotItem">
								<template v-if="typeof slotItem === 'object'">
									<template v-for="(record_name, index) in Object.keys(slotItem)" :key="index">
										<template
											v-if="
												typeof slotItem[record_name] === 'object' &&
												recordResponse(slotItem, index)
											"
										>
											<lazy-a-form-item
												:name="[slotIndex, record_name, 'value']"
												:required="recordResponse(slotItem, index)?.required"
												:rules="recordResponse(slotItem, index)?.validations"
												:label="toCapitalize(record_name).replaceAll('_', ' ')"
												:colon="false"
											>
												<slot
													:name="record_name"
													:records="slotItem"
													:record="slotItem[record_name]"
													:index="index"
													:set-record="recordResponse"
												>
													<lazy-a-select
														v-if="recordResponse(slotItem, index)?.type === 'select'"
														v-model:value="slotItem[record_name].value"
														:options="recordResponse(slotItem, index)?.options || []"
														:name="record_name"
														:aria-label="`${record_name}-${index}`"
														:data-testid="`${record_name}-${index}`"
														:placeholder="
															recordResponse(slotItem, index)?.placeholder || '-'
														"
														:class="[recordResponse(slotItem, index)?.class || '']"
														:show-search="recordResponse(slotItem, index)?.searchable"
														:disabled="recordResponse(slotItem, index)?.disabled"
														:filter-option="filterSelectOption"
														@change="
															(e: SelectValue) => {
																if (
																	typeof recordResponse(slotItem, index)?.onChange ===
																	'function'
																) {
																	;(
																		recordResponse(slotItem, index)
																			?.onChange as Function
																	)(e, index)
																}
															}
														"
													/>

													<lazy-a-input
														v-else
														:id="record_name as string"
														v-model:value="slotItem[record_name].value"
														v-number="
															['tel', 'value', 'phone'].includes(
																recordResponse(slotItem, index)?.type || ''
															)
														"
														v-format="recordResponse(slotItem, index)?.format"
														v-naira="recordResponse(slotItem, index)?.currencyFormat"
														autocomplete="none"
														:aria-label="record_name"
														:data-testid="`${record_name}-${index}`"
														:maxlength="recordResponse(slotItem, index)?.validations?.max"
														:type="
															(recordResponse(slotItem, index)?.type as RecordSchema) ||
															'text'
														"
														:name="record_name as string"
														:placeholder="
															recordResponse(slotItem, index)?.placeholder || '-'
														"
														:inputmode="
															recordResponse(slotItem, index)?.inputMode || 'text'
														"
														:disabled="recordResponse(slotItem, index)?.disabled"
														class="custom-input placeholder:font-extrabold placeholder:text-gray-950"
														:class="[recordResponse(slotItem, index)?.class || '']"
														@change="
															(e: Event) => {
																if (
																	typeof recordResponse(slotItem, index)?.onChange ===
																	'function'
																) {
																	;(
																		recordResponse(slotItem, index)
																			?.onChange as Function
																	)(e, index)
																}
															}
														"
													/>
												</slot>
											</lazy-a-form-item>
										</template>
									</template>
								</template>
							</slot>
							<div class="py-4">
								<a-button
									html-type="button"
									class="h-[44px] w-full max-w-[167px] !rounded-lg !bg-primary-2 !font-medium !text-white disabled:!bg-primary-300"
									:loading="slotItem?.loading"
									:disabled="slotItem?.loading || isRecordEmpty(slotItem)"
									@click="addField(slotItem, slotIndex)"
								>
									{{ slotItem?.loading ? 'Verifying...' : 'Add Staff' }}
								</a-button>
							</div>
						</div>
					</li>
				</template>
			</ul>
			<div
				class="fixed bottom-5 left-0 right-0 z-[200] mx-auto flex w-full max-w-[564px] items-center justify-center px-5"
			>
				<a-button
					class="default-btn w-full justify-center bg-primary-2 text-white"
					:disabled="!isAnItemDone || submitting"
					:loading="submitting"
					data-testid="add to plan"
					@click="saveForm"
				>
					{{ submitting ? 'Saving...' : 'Save and Continue' }}
				</a-button>
			</div>
		</a-form>
	</div>
</template>

<style scoped>
	.custom-input {
		@apply !px-3 focus:!border-primary-2 disabled:!bg-white disabled:!font-medium disabled:!text-gray-600;
	}
</style>
